import getDaysInMonth from 'date-fns/getDaysInMonth';
import isSameMonth from 'date-fns/isSameMonth';

import type { AutomationFreeLimitStatus, Edition, TraitsForContext } from '../types';

import type { EditionAwarenessContextType } from './edition-awareness-context';

export const AUTOMATION_USAGE_FREE_LIMIT = 100;
export const AUTOMATION_USAGE_STANDARD_LIMIT = 1700;
export const PROJECTED_USAGE_WARNING_THRESHOLD: number = 0.8 * AUTOMATION_USAGE_FREE_LIMIT;

export type GetAutomationFreeLimitStatusArgs = Pick<
	TraitsForContext,
	'automationUsage' | 'lastAutomationFreeLimitBreachDate'
> &
	Pick<EditionAwarenessContextType, 'edition' | 'product' | 'isInTrial'>;

export const getAutomationFreeLimitStatus = ({
	edition,
	product,
	automationUsage,
	lastAutomationFreeLimitBreachDate = null,
}: GetAutomationFreeLimitStatusArgs): AutomationFreeLimitStatus => {
	if (edition === 'free' && product === 'jira') {
		if (hasBreachedAutomationFreeLimit(automationUsage, lastAutomationFreeLimitBreachDate)) {
			return 'BREACHED';
		}
		if (isAutomationProjectedToBreach(automationUsage, edition)) {
			return 'PROJECTED_TO_BREACH';
		}
	}
	return null;
};

export const getProjectedAutomationUsage = (automationUsage: number): number => {
	const today = new Date();
	const dayOfMonth = today.getDate();
	return (automationUsage / dayOfMonth) * getDaysInMonth(today);
};

const isAutomationProjectedToBreach = (automationUsage: number, edition: Edition) => {
	// Already breached the limit, no need to project
	if (automationUsage >= AUTOMATION_USAGE_FREE_LIMIT) {
		return false;
	}

	const projectedUsage = getProjectedAutomationUsage(automationUsage);

	if (edition === 'free') {
		return (
			projectedUsage >= AUTOMATION_USAGE_FREE_LIMIT &&
			automationUsage >= PROJECTED_USAGE_WARNING_THRESHOLD
		);
	}
	return projectedUsage >= AUTOMATION_USAGE_FREE_LIMIT;
};

export const hasBreachedAutomationFreeLimit = (
	automationUsage: number,
	lastAutomationFreeLimitBreachDate: Date | null,
): boolean | null => {
	return (
		lastAutomationFreeLimitBreachDate &&
		automationUsage >= AUTOMATION_USAGE_FREE_LIMIT &&
		isSameMonth(new Date(), lastAutomationFreeLimitBreachDate)
	);
};
